/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router';
import { shape, string, object } from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import { useQuery } from '@apollo/client';
import Cookies from 'universal-cookie';
import { getSSRDeviceType } from '../../../../../../state/ducks/App/App-Selectors';
import { getBrand } from '../../../../../../state/ducks/App/ducks/Config/Config-Selectors';
import {
    getTalonOneRewardsLoading,
    getTalonOneRewardsError,
    getTalonOneRewardsData,
} from '../../../../../../state/ducks/Member/ducks/TalonOneRewards/TalonOneRewards-Selectors';
import { fetchTalonOneRewards, clearTalonOneRewards } from '../../../../../../state/ducks/Member/ducks/TalonOneRewards/TalonOneRewards-Actions';
import { getIsAuthenticatedStatus } from '../../../../../../state/ducks/Member/ducks/Common/Common-Selectors';
import findContent from '../../../../../gql/queries/findContent';
import { useAuth } from '../../../../../../state/ducks/Member/ducks/Auth/Plugin/auth';
import { trackEvent } from '../../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';

const styles = ({ breakpoints }) => ({
    accountBannerHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px 8px',
        // backgroundColor is dynamically set via inline style
        height: 'auto !important',
        fontSize: '16px!important',
        gap: '8px',
        // Mobile padding
        [breakpoints.down('sm')]: {
            padding: '16px',
            fontSize: '14px!important',
        },
    },
    bannerIcon: {
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        '& img': {
            width: '41px',
            height: '39px',
        },
    },
    bannerTextContent: {
        display: 'flex',
        flexDirection: 'column',
        textAlign: 'center',
        wordBreak: 'break-word',
        '& p': {
            margin: '0px!important',
            whiteSpace: 'nowrap',
        },
    },
    accountNameText: {
        fontFamily: 'museo-sans, "Area Variable", "Lato", sans-serif',
        fontWeight: 500,
        lineHeight: '24px',
        letterSpacing: '0.01em',
        margin: 0,
        textTransform: 'capitalize',
    },
    creditText: {
        fontFamily: 'museo-sans, "Area Variable", "Lato", sans-serif',
        fontWeight: 400,
        fontSize: '15px!important',
        // color is now dynamically set via inline style
        margin: 0,
    },
    creditHighlight: {
        fontFamily: 'museo-sans, "Area Variable", "Lato", sans-serif',
        fontWeight: 700,
        fontSize: '15px !important',
        textAlign: 'center',
        // color is now dynamically set via inline style
    },
    secondaryCreditText: {
        fontSize: '13px !important',
    },
    homepageBannerText: {
        display: 'flex',
        height: '43px',
        justifyContent: 'center',
        alignItems: 'flex-start',
        gap: '16px',
        flexShrink: 0,
        alignSelf: 'stretch',
        textAlign: 'center',
        fontFamily: 'ivypresto-text, "Crimson Text", "Loretta VF", "Playfair Display", Georgia, serif',
        fontSize: '26px',
        fontStyle: 'normal',
        fontWeight: 300,
        lineHeight: 'normal',
    },
    homepageBanner: {
        display: 'flex',
        width: 'auto',
        height: '167px',
        padding: '13px 16px',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '10px',
        backgroundBlendMode: 'hard-light, multiply',
        [breakpoints.down('sm')]: {
            height: '146px',
            padding: '11px 16px',
            gap: '4px',
        },
    },
    unauthenticatedLink: {
        background: 'none',
        border: 'none',
        textDecoration: 'underline',
        cursor: 'pointer',
        padding: 0,
        color: 'var(--Primary-Black, #000)',
        textAlign: 'CenterFocusStrong',
        fontFamily: 'museo-sans, "Area Variable"CenterFocusStrong',
        fontSize: '13pxCenterFocusStrong',
        fontStyle: 'normalCenterFocusStrong',
        fontWeight: '600CenterFocusStrong',
        lineHeight: '19pxCenterFocusStrong', /* 146.154% */
        textDecorationLine: 'underlineCenterFocusStrong',
        textDecorationStyle: 'solidCenterFocusStrong',
        textDecorationSkipInk: 'autoCenterFocusStrong',
        textDecorationThickness: 'autoCenterFocusStrong',
        textUnderlineOffset: 'autoCenterFocusStrong',
        textUnderlinePosition: 'from-fontCenterFocusStrong',
    },
    homepageTalonOneBanner: {
        display: 'flex',
        width: '352px',
        padding: '16px',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '16px',
    },
});

/**
 * TalonOneBanner - Self-contained banner component that fetches and displays
 * TalonOne rewards data. Only renders when the user has available rewards.
 *
 * Uses Redux with Saga for rewards data fetching.
 * Uses Apollo/GraphQL for ContentStack styling content.
 * Automatically fetches rewards when user is authenticated & navigates to account page.
 */
const TalonOneBanner = ({
    classes,
    firstName,
    user,
    pageType,
}) => {
    const cookies = new Cookies();
    const campaignBanner = cookies.get('banner')?.c;
    const location = useLocation();
    const pathname = location?.pathname;
    const urlParams = location?.search?.toLowerCase() || '';
    const isHomepage = pathname === '/' || pathname === '/foryou';
    const dispatch = useDispatch();
    const { loginByRedirect } = useAuth();
    const device = useSelector(getSSRDeviceType);
    const brand = useSelector(getBrand);
    const escapedBannerCookie = campaignBanner?.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') || '';
    const graphqlSafeBannerCookie = escapedBannerCookie.replace(/\\/g, '\\\\');

    // Redux selectors for rewards data
    const isLoading = useSelector(getTalonOneRewardsLoading);
    const isError = useSelector(getTalonOneRewardsError);
    const rewardsData = useSelector(getTalonOneRewardsData);
    const isAuthenticated = useSelector(getIsAuthenticatedStatus);

    // Sum currentBalance from all loyalty programs
    const programs = rewardsData?.loyalty?.programs || {};
    const rewardsBalance = Object.values(programs).reduce(
        (sum, program) => sum + (program?.ledger?.currentBalance || 0),
        0,
    );

    // Fetch Talon One banner content from ContentStack
    const TALON_ONE_BANNER_QUERY = findContent({
        brand: 'global',
        locale: 'en-us',
        contentType: 'talon_one',
        query: `\\"brand\\" : \\"${brand.domain}\\", \\"campaign_id\\" : { \\"$regex\\" : \\".*${graphqlSafeBannerCookie}.*\\", \\"$options\\" : \\"i\\" }`,
    });

    const { data: bannerContentData, loading: isBannerContentLoading } = useQuery(TALON_ONE_BANNER_QUERY);

    // Fetch rewards data on every component mount when user is authenticated
    // This ensures fresh data on page refresh or component re-render
    useEffect(() => {
        if (isAuthenticated) {
            dispatch(fetchTalonOneRewards());
        }

        // Clear rewards data when component unmounts to ensure fresh fetch next time
        return () => {
            dispatch(clearTalonOneRewards());
        };
    }, [dispatch, isAuthenticated, pathname, pageType]);

    // is Talon One campaign. compares campaign banner to campaign id in CMS
    const campaignId = bannerContentData?.findContent?.content?.entries?.[0]?.campaign_id;
    const campaignIDList = campaignId?.split(',').map((id) => id.trim()) || [];
    const normalizedCampaignBanner = campaignBanner?.trim();
    const talonOneCampaign = (campaignId && campaignIDList.includes(normalizedCampaignBanner)) || urlParams?.includes('talonone');
    let isTalonOneCampaign;
    if (typeof window !== 'undefined') {
        isTalonOneCampaign = JSON.parse(window.localStorage.getItem('isTalonOneCampaign'));
    }

    useEffect(() => {
        if ((isHomepage || pageType === 'homepage' || pageType === 'product') && (talonOneCampaign || isTalonOneCampaign)) {
            if (!isAuthenticated) {
                dispatch(trackEvent({
                    eventCategory: `${pageType === 'product' ? 'PDP' : 'Homepage'}`,
                    eventAction: 'Talon One Sign In Prompt',
                    eventLabel: 'Displayed',
                    feature_category: `${pageType === 'product' ? 'PDP' : 'Homepage'}`,
                    feature_element: 'Talon One Sign In Prompt',
                    feature_detail: 'Displayed',
                }));
            }
        }
    }, [pageType]);

    // Extract my_account content from GraphQL response
    const myAccountContent = bannerContentData?.findContent?.content?.entries?.[0]?.my_account;
    const myAccountStyling = bannerContentData?.findContent?.content?.entries?.[0]?.styling;
    // multibrand cms styling
    const brandStyle = myAccountStyling?.brand?.find((item) => item?.brand_name === brand?.domain);
    const renderOnHomepage = (talonOneCampaign || isTalonOneCampaign) && (isHomepage || pageType === 'homepage');

    const backgroundColor = myAccountStyling?.background_color || '#EFF9F6';
    const homepageBackgroundColor = myAccountStyling?.homepage_background_color || '#FFF';
    const homepageBannerTextColor = brandStyle?.banner_text_color || '#FFFFFF';
    const backgroundImage = brandStyle?.banner_image?.url || null;
    const creditColor = myAccountStyling?.credit_color || '#387154';
    const textColor = myAccountStyling?.text_color || '#000000';
    const iconUrl = myAccountContent?.icon?.url || null;
    const creditMessage = myAccountContent?.credit_message || 'You have a {{credit}} credit to use today!';
    const secondaryCreditMessage = myAccountContent?.secondary_credit_message || 'Redeem at checkout';
    const unauthenticatedSecondaryCreditMessage = (
        <p>
            <button
                type="button"
                className={classes.unauthenticatedLink}
                onClick={async () => {
                    dispatch(trackEvent({
                        eventCategory: `${pageType === 'product' ? 'PDP' : 'Homepage'}`,
                        eventAction: 'Talon One Sign In Prompt',
                        eventLabel: 'Clicked',
                        feature_category: `${pageType === 'product' ? 'PDP' : 'Homepage'}`,
                        feature_element: 'Talon One Sign In Prompt',
                        feature_detail: 'Clicked',
                    }));

                    await loginByRedirect();
                }}
            >
                Sign in
            </button> to redeem
        </p>
    );
    const talonOneBannerMessage = isAuthenticated ? `Welcome back, ${user?.firstName}!` : 'Our Gift to You';

    // Don't render if no rewards or Contentstack query loading || error
    if ((isAuthenticated && rewardsBalance <= 0) || isLoading || isError) {
        return null;
    }

    if (renderOnHomepage && (isBannerContentLoading || !myAccountStyling)) {
        return null;
    }

    // Split credit message by placeholder
    // Handle all patterns: {credit}, ${credit}, {{credit}}, ${{credit}}
    const parts = creditMessage?.split(/\$?\{\{?credit\}\}?/) || '';
    const beforeCredit = parts[0] || '';
    const afterCredit = parts[1] || '';
    const creditAmount = `$${!isAuthenticated && (talonOneCampaign || isTalonOneCampaign) ? 15 : rewardsBalance.toFixed(2)} `;

    const banner = () => (
        <div
            className={(isHomepage || pageType === 'homepage') ? classes.homepageTalonOneBanner : classes.accountBannerHeader}
            style={{ backgroundColor }}
            role="region"
            aria-label={`${firstName}'s account - You have waiting`}
        >
            <div className={classes.bannerTextContent}>
                <p className={classes.creditText} style={{ color: textColor }}>
                    {beforeCredit}
                    <span className={classes.creditHighlight} style={{ color: creditColor }}>
                        {creditAmount}
                    </span>
                    {afterCredit}
                </p>
                <p className={`${classes.creditText} ${classes.secondaryCreditText}`} style={{ color: textColor }}>
                    {isAuthenticated ? secondaryCreditMessage : unauthenticatedSecondaryCreditMessage}
                </p>
            </div>
            {iconUrl && (
                <div className={classes.bannerIcon}>
                    <img src={iconUrl} alt="Rewards icon" />
                </div>
            )}
        </div>
    );

    return (
        <>
            {renderOnHomepage && (
                <div
                    className={classes.homepageBanner}
                    style={{
                        background: backgroundImage && `url(${backgroundImage}) lightgray 50% / cover no-repeat`,
                        backgroundColor: backgroundImage ? '' : homepageBackgroundColor,
                        marginBottom: isAuthenticated && device === 'mobile' && '40px',
                    }}
                >
                    <p className={classes.homepageBannerText} style={{ color: homepageBannerTextColor }}>{talonOneBannerMessage}</p>
                    {banner()}
                </div>
            )}
            {(talonOneCampaign || isTalonOneCampaign) && (!isHomepage || pageType === 'product') && banner()}
        </>
    );
};

TalonOneBanner.defaultProps = {
    firstName: '',
    user: {},
    pageType: '',
};

TalonOneBanner.propTypes = {
    classes: shape({}).isRequired,
    firstName: string,
    user: object,
    pageType: string,
};

export default withStyles(styles)(TalonOneBanner);
